import type { BusinessCalendar } from "@helpdesk/sla";

import { Avatar } from "../ui/Avatar";
import { Badge } from "../ui/Badge";
import { cn } from "../ui/cn";
import { SlaIndicator } from "../SlaClock";
import { assigneeOf } from "../../lib/agents";
import { useLiveSla } from "../../lib/sla";
import { shortDateTime } from "../../lib/time";
import type { Pipeline, TicketSummary, User } from "../../types";

interface TicketCardProps {
  ticket: TicketSummary;
  pipelines: Pipeline[];
  /** The agent list, for the assignee's chosen avatar tint. */
  users: User[];
  calendar: BusinessCalendar;
  selected: boolean;
  /** Customer mail on this ticket the signed-in agent has not seen yet. */
  unread: boolean;
  onSelect: (id: number) => void;
  /**
   * Opens the assignee picker for this ticket. When given, the avatar becomes
   * its own button; without it the card is one tap target, as it always was.
   */
  onAssign?: (ticket: TicketSummary) => void;
}

/**
 * One ticket as a card, for phone widths.
 *
 * The table's seven columns cannot be made to fit, and a queue that scrolls
 * sideways is unusable one-handed. The card keeps what triage actually needs --
 * who it is from, what they said, how long is left -- and drops the two
 * in-place editors, which want a pointer and are both available inside the
 * ticket anyway.
 *
 * A plain button rather than the row's nested click targets: the copy-to-
 * clipboard affordances would be interactive elements inside an interactive
 * element, which is invalid and unpredictable under a screen reader. Tapping a
 * card opens the ticket.
 *
 * The one exception is the avatar, which hands the ticket to somebody else.
 * Triaging a queue on a phone is mostly "that one's Sam's", and opening each
 * ticket to say so was three screens per ticket. It is a sibling of the card's
 * button rather than nested inside it, for the reason above, and it is a full
 * 44px square even though the circle in it is 32 -- the circle is what is
 * seen, the square is what a thumb actually hits.
 */
export function TicketCard({
  ticket,
  pipelines,
  users,
  calendar,
  selected,
  unread,
  onSelect,
  onAssign,
}: TicketCardProps) {
  const sla = useLiveSla(ticket, pipelines, calendar);
  const pipeline = pipelines.find((entry) => entry.id === ticket.pipelineId);

  const avatar = (
    <Avatar
      size="md"
      agent={assigneeOf(users, ticket.assigneeId, ticket.assigneeName)}
    />
  );

  return (
    <div
      className={cn(
        "flex w-full items-start",
        // Banded like the desktop rows, for the same reason and so the two
        // layouts do not read as two different products.
        selected
          ? "bg-brand-50 dark:bg-brand-950/40"
          : "odd:bg-surface even:bg-app",
      )}
    >
      {onAssign ? (
        <button
          type="button"
          onClick={() => onAssign(ticket)}
          aria-label={`Assign #${ticket.id} — now ${ticket.assigneeName ?? "unassigned"}`}
          title="Assign to…"
          className={cn(
            "ml-1.5 mt-1.5 flex size-11 shrink-0 items-center justify-center rounded-full",
            "active:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        >
          {avatar}
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => onSelect(ticket.id)}
        className={cn(
          "flex min-w-0 flex-1 items-start gap-3 py-3 pr-3 text-left active:bg-surface-2",
          onAssign ? "pl-1.5" : "pl-3",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40",
        )}
      >
        {onAssign ? null : avatar}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-sm text-content",
                unread ? "font-bold" : "font-medium",
              )}
            >
              {ticket.companyName ?? ticket.contactName ?? "Unknown"}
            </span>
            <span
              className={cn(
                "shrink-0 text-[11px] tabular-nums",
                unread ? "font-semibold text-content" : "text-faint",
              )}
            >
              {shortDateTime(ticket.lastMessageAt)}
            </span>
          </div>

          <div
            /* Two lines here where the table gets one: a card has the height to
               spare, and a phone has no hover to fall back on. */
            className={cn(
              "line-clamp-2 text-sm",
              unread ? "font-semibold text-content" : "text-muted",
            )}
          >
            {ticket.subject}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <SlaIndicator sla={sla} />
            {pipeline ? (
              <Badge color={pipeline.color} className="max-w-full">
                <span className="truncate">{pipeline.name}</span>
              </Badge>
            ) : null}
            <span className="text-[11px] tabular-nums text-faint">
              #{ticket.id}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}
